import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { Button, Input, Autocomplete, AutocompleteItem } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { getCountries } from "../../../utils/countryUtils";
import addressImgModal from "../../../address-modal.png";
import { IoClose } from "react-icons/io5";
import { useUseCases } from "../../../context/UseCasesProvider";

type Contact = {
  phone?: string;
  email?: string;
  name?: string;
};

export type AddressData = {
  address: string;
  address_name: string;
  city: string;
  complement: string;
  country: string;
  postal_code: string;
  street_number: string;
  contact?: Contact[];
};

const addPart = (parts: string[], value: string, separator: string = ", "): void => {
  if (parts.length > 0) {
    parts.push(separator);
  }
  parts.push(value);
};

export const formatAddressDisplay = (addressData: AddressData | null): string => {
  if (!addressData) return "";

  const result: string[] = [];

  if (addressData.address_name) {
    result.push(addressData.address_name);
  }

  const addressParts: string[] = [];
  if (addressData.street_number) addressParts.push(addressData.street_number);
  if (addressData.address) addressParts.push(addressData.address);
  if (addressData.complement) addressParts.push(addressData.complement);

  if (addressParts.length > 0) {
    addPart(result, addressParts.join(" "), " - ");
  }

  const cityParts: string[] = [];
  if (addressData.postal_code) cityParts.push(addressData.postal_code);
  if (addressData.city) cityParts.push(addressData.city);

  if (cityParts.length > 0) {
    addPart(result, cityParts.join(" "));
  }

  if (addressData.country) {
    addPart(result, addressData.country);
  }

  return result.join("");
};

type FormValues = {
  address_name: string;
  street_number: string;
  address: string;
  complement: string;
  postal_code: string;
  city: string;
  country: string;
  phone: string;
  name: string;
  email: string;
};

interface AddressModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: AddressData) => void;
  readonly initialData?: AddressData | null;
  readonly classNames?: Record<string, string>;
}

export default function AddressModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  classNames,
}: AddressModalProps) {
  const { t } = useTranslation();
  const settingsInterface = useUseCases();
  const deliveryDisplay = settingsInterface?.settings?.display?.metadata_delivery;
  const showPhone = deliveryDisplay?.display_phone_extract;
  const showName = deliveryDisplay?.display_name_extract;
  const showEmail = deliveryDisplay?.display_email_extract;

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      address_name: initialData?.address_name || "",
      street_number: initialData?.street_number || "",
      address: initialData?.address || "",
      complement: initialData?.complement || "",
      postal_code: initialData?.postal_code || "",
      city: initialData?.city || "",
      country: initialData?.country || "",
      phone: initialData?.contact?.[0]?.phone || "",
      name: initialData?.contact?.[0]?.name || "",
      email: initialData?.contact?.[0]?.email || "",
    },
  });

  const countryOptions = useMemo(() => {
    const locale = t("locale") || "en";
    return getCountries(locale);
  }, [t]);

  const isFieldInvalid = (hasError: boolean, fieldValue: string) => {
    return hasError || (!fieldValue && isOpen);
  };

  const getInputClassNames = (hasError: boolean, fieldValue: string) => ({
    ...classNames,
    inputWrapper: [
      classNames?.inputWrapper,
      isFieldInvalid(hasError, fieldValue) && "!border !border-red-500 !bg-default-100 data-[hover=true]:!bg-default-200 data-[focus=true]:!bg-default-100 group-data-[focus=true]:!bg-default-100"
    ].filter(Boolean).join(" ")
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        address_name: initialData?.address_name || "",
        street_number: initialData?.street_number || "",
        address: initialData?.address || "",
        complement: initialData?.complement || "",
        postal_code: initialData?.postal_code || "",
        city: initialData?.city || "",
        country: initialData?.country || "",
        phone: initialData?.contact?.[0]?.phone || "",
        name: initialData?.contact?.[0]?.name || "",
        email: initialData?.contact?.[0]?.email || "",
      });
      trigger();
    }
  }, [isOpen, initialData, reset, trigger]);

  const handleFormSubmit: SubmitHandler<FormValues> = (data) => {
    const formattedData: AddressData = {
      address_name: data.address_name,
      street_number: data.street_number,
      address: data.address,
      complement: data.complement,
      postal_code: data.postal_code,
      city: data.city,
      country: data.country,
      contact: [{
        ...(showPhone && { phone: data.phone }),
        ...(showName && { name: data.name }),
        ...(showEmail && { email: data.email }),
      }],
    };
    onSubmit(formattedData);
    onClose();
  };

  return createPortal(
    <div
      className={`fixed inset-y-0 right-0 z-50 w-[450px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(handleFormSubmit)(e);
        }}
        className="flex flex-col h-full"
      >
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div className="flex-1">
            <div className="text-2xl font-bold pb-2">{t("enter_your_address")}</div>
            <div className="text-vistagreen text-sm">{t("fill_required_info_for_address")}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <IoClose className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          
          <div className="flex flex-col gap-4">
            <Controller
              name="address_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  label={t("address_name")}
                  placeholder={t("address_name")}
                  variant="flat"
                  labelPlacement="outside"
                  classNames={classNames}
                />
              )}
            />

            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-1">
                <Controller
                  name="street_number"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      label={t("street_number")}
                      placeholder={t("street_number")}
                      variant="flat"
                      labelPlacement="outside"
                      classNames={classNames}
                    />
                  )}
                />
              </div>
              <div className="col-span-3">
                <Controller
                  name="address"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      label={t("address")}
                      placeholder={t("address")}
                      variant="flat"
                      labelPlacement="outside"
                      isRequired
                      isInvalid={isFieldInvalid(!!errors.address, field.value)}
                      classNames={getInputClassNames(!!errors.address, field.value)}
                    />
                  )}
                />
              </div>
            </div>

            <Controller
              name="complement"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  label={t("complement")}
                  placeholder={t("complement")}
                  variant="flat"
                  labelPlacement="outside"
                  classNames={classNames}
                />
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="postal_code"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    label={t("zip_code")}
                    placeholder={t("zip_code")}
                    variant="flat"
                    labelPlacement="outside"
                    isRequired
                    isInvalid={isFieldInvalid(!!errors.postal_code, field.value)}
                    classNames={getInputClassNames(!!errors.postal_code, field.value)}
                  />
                )}
              />

              <Controller
                name="city"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    label={t("city")}
                    placeholder={t("city")}
                    variant="flat"
                    labelPlacement="outside"
                    isRequired
                    isInvalid={isFieldInvalid(!!errors.city, field.value)}
                    classNames={getInputClassNames(!!errors.city, field.value)}
                  />
                )}
              />
            </div>

            <Controller
              name="country"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  label={t("country")}
                  placeholder={t("country")}
                  variant="flat"
                  labelPlacement="outside"
                  isRequired
                  isInvalid={isFieldInvalid(!!errors.country, field.value)}
                  selectedKey={field.value}
                  onSelectionChange={(key) => {
                    field.onChange(key);
                  }}
                  classNames={{
                    ...classNames,
                    base: classNames?.base,
                    listboxWrapper: "max-h-[320px]",
                    selectorButton: classNames?.selectorButton,
                    popoverContent: classNames?.popoverContent,
                  }}
                  inputProps={{
                    classNames: {
                      inputWrapper: [
                        classNames?.inputWrapper,
                        isFieldInvalid(!!errors.country, field.value) && "!border !border-red-500 !bg-default-100 data-[hover=true]:!bg-default-200 data-[focus=true]:!bg-default-100 group-data-[focus=true]:!bg-default-100"
                      ].filter(Boolean).join(" ")
                    }
                  }}
                >
                  {countryOptions.map((country) => (
                    <AutocompleteItem key={country.code}>
                      {country.label}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              )}
            />

            {showName && (
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    label={t("name")}
                    placeholder={t("name")}
                    variant="flat"
                    labelPlacement="outside"
                    classNames={classNames}
                  />
                )}
              />
            )}

            {showEmail && (
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="email"
                    label={t("email")}
                    placeholder={t("email")}
                    variant="flat"
                    labelPlacement="outside"
                    classNames={classNames}
                  />
                )}
              />
            )}

            {showPhone && (
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="tel"
                    label={t("phone")}
                    placeholder={t("phone")}
                    variant="flat"
                    labelPlacement="outside"
                    classNames={classNames}
                  />
                )}
              />
            )}
          </div>

          <div className="flex justify-center mb-6">
            <img
              src={addressImgModal}
              alt={t("alt_vistaflow_user")}
              className="w-32"
            />
          </div>
          
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t mb-14 border-gray-200">
          <Button color="default" variant="flat" onPress={onClose}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
            className="bg-vistagreen text-white disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("submit_address")}
          </Button>
        </div>
      </form>
    </div>,
    document.body
  );
}
